import { BonesMap, EnhancedGLTF } from '@cartwhl/3d/gltf/types';
import { useEffect, useState } from 'react';
import * as THREE from 'three';
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import { createSignedUrlLoadingManager } from '../utils/signedUrlCache';

export default function useGLTFWithCredentials(
  path?: Readonly<string>,
  setLoading?: (loading: boolean) => void,
  modifiers?: Array<(model: GLTF) => void>,
) {
  const [model, setModel] = useState<EnhancedGLTF | null>(null);
  const [error, setError] = useState<ErrorEvent | null>(null);

  useEffect(() => {
    if (!path) {
      return;
    }
    console.log('Loading the GLTF path at:', path);
    setLoading?.(true);

    let cancelled = false;

    loadAndEnrichGLTF(path, modifiers)
      .then((gltf) => {
        if (cancelled) return;
        setModel(gltf);
        setLoading?.(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
        setLoading?.(false);
      });

    return () => {
      cancelled = true;
    };
  }, [path]);

  if (error) {
    throw error;
  }
  return model;
}

// Separate loading function that can be used anywhere
export async function loadAndEnrichGLTF(
  modelPath: string,
  modifiers?: Array<(model: GLTF) => void>,
): Promise<EnhancedGLTF> {
  const manager = createSignedUrlLoadingManager();
  const gltfLoader = new GLTFLoader(manager);
  gltfLoader.setWithCredentials(true);

  // Load the GLTF file
  const gltf = await new Promise<GLTF>((resolve, reject) => {
    gltfLoader.load(
      modelPath,
      (gltf) => resolve(gltf),
      undefined,
      (error) => reject(error),
    );
  });

  // Load bones map file
  const bonesMapPath = modelPath.replace('.glb', '.json');
  let bonesMap: BonesMap = {};
  try {
    const bonesMapResponse = await fetch(bonesMapPath, {
      credentials: 'include',
    });
    if (bonesMapResponse.ok) {
      const data = await bonesMapResponse.json();
      bonesMap = data.joints;
    }
  } catch (err) {
    // No bones map found
  }

  // Traverse scene and save bone map data to userData
  gltf.scene.traverse((object: THREE.Object3D) => {
    if (object instanceof THREE.Bone && object.name) {
      const originalName = bonesMap[object.name] || object.name;
      if (originalName) {
        object.userData.originalName = originalName;
        object.userData.autoAssign = object.name;
      }
    }
  });

  // Apply modifiers if any
  if (modifiers) {
    for (const modify of modifiers) {
      modify(gltf);
    }
  }

  // Create nodes and materials maps
  const nodes: { [key: string]: THREE.Object3D } = {};
  const materials: { [key: string]: THREE.Material } = {};

  gltf.scene.traverse((object: THREE.Object3D) => {
    if (object.name) {
      nodes[object.name] = object;
    }

    const mesh = object as THREE.Mesh;
    if (mesh.material) {
      const material = mesh.material as THREE.Material;
      if (material.name) {
        materials[material.name] = material;
      }
    }
  });
  // Create meshes map
  const meshes: { [key: string]: THREE.Mesh } = {};
  gltf.scene.traverse((object: THREE.Object3D) => {
    if (object instanceof THREE.Mesh && object.name) {
      meshes[object.name] = object;
    }
  });

  // Return enhanced GLTF with nodes, materials and meshes maps
  return {
    ...gltf,
    nodes,
    materials,
    meshes,
    bonesMap,
  };
}
